import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Users, Download, Grid, CalendarDays, ChevronDown, ChevronUp, ZoomIn } from 'lucide-react';
import { API } from '../App';
import ImageLightbox from '../components/ImageLightbox';

// Helper to parse date string without timezone shift
const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  // If it's just a date (YYYY-MM-DD), parse as local date
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  // Otherwise parse normally
  return new Date(dateStr);
};

// Helper to format time to 12-hour AM/PM format
const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  // If already contains AM/PM, return as is
  if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) {
    return timeStr;
  }
  // Parse 24-hour format (HH:MM)
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function Events({ user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [registerModal, setRegisterModal] = useState({ show: false, event: null });
  const [formData, setFormData] = useState({ name: '', email: '' });
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ url: '', alt: '' });

  const openLightbox = (imageUrl, imageAlt) => {
    setLightboxImage({ url: imageUrl, alt: imageAlt });
    setLightboxOpen(true);
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerModal.event) return;

    try {
      await axios.post(`${API}/events/${registerModal.event.id}/register`, {
        event_id: registerModal.event.id,
        user_name: formData.name,
        user_email: formData.email
      });
      toast.success('Registration successful!');
      setRegisterModal({ show: false, event: null });
      setFormData({ name: '', email: '' });
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleDownloadCalendar = async (eventId) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/ical`);
      const icalContent = response.data.ical;
      
      const blob = new Blob([icalContent], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'event.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Calendar file downloaded!');
    } catch (error) {
      toast.error('Failed to download calendar file');
    }
  };

  const upcomingEvents = events.filter(e => !e.is_recurring);
  const recurringEvents = events.filter(e => e.is_recurring);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-2xl" style={{ color: '#1e3a8a' }}>Loading events...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <div className="section-padding bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="events-title">
            Events & Activities
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto mb-6">
            Join us for worship, fellowship, and community
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => setViewMode('list')}
              className={`px-6 py-3 rounded-full text-lg font-semibold ${viewMode === 'list' ? 'btn-secondary' : 'bg-white text-blue-900'}`}
              data-testid="list-view-btn"
            >
              <Grid size={20} className="inline mr-2" />
              List View
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-6 py-3 rounded-full text-lg font-semibold ${viewMode === 'calendar' ? 'btn-secondary' : 'bg-white text-blue-900'}`}
              data-testid="calendar-view-btn"
            >
              <CalendarDays size={20} className="inline mr-2" />
              Calendar View
            </button>
          </div>
        </div>
      </div>

      {/* Upcoming Events - List View */}
      {viewMode === 'list' && (
        <div className="section-padding bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl md:text-5xl font-bold mb-12" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }} data-testid="upcoming-events-title">
              Upcoming Special Events
            </h2>
            {upcomingEvents.length === 0 ? (
              <p className="text-xl text-center text-slate-600">No upcoming special events at this time.</p>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {(showAllEvents ? upcomingEvents : upcomingEvents.slice(0, 4)).map((event) => (
                    <div key={event.id} className="card p-5" data-testid={`event-${event.id}`}>
                      {event.image_url ? (
                        <div 
                          className="relative group cursor-pointer mb-3 overflow-hidden rounded-lg bg-slate-100"
                          onClick={() => openLightbox(event.image_url, event.title)}
                          data-testid={`event-image-${event.id}`}
                        >
                          <div className="aspect-[4/3] w-full">
                            <img src={event.image_url} alt={event.title} className="w-full h-full object-contain bg-white transition-transform duration-300 group-hover:scale-105" />
                          </div>
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center rounded-lg">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full p-2 shadow-lg">
                              <ZoomIn size={20} className="text-blue-900" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="aspect-[4/3] w-full rounded-lg mb-3 bg-gradient-to-br from-blue-100 to-amber-100 flex items-center justify-center">
                          <Calendar size={48} className="text-blue-300" />
                        </div>
                      )}
                      <h3 className="text-xl font-bold mb-2" style={{ color: '#1e3a8a' }}>{event.title}</h3>
                      <div className="space-y-1 mb-3 text-sm">
                        <div className="flex items-center text-slate-600">
                          <Calendar size={16} className="mr-2" color="#f59e0b" />
                          {parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div className="flex items-center text-slate-600">
                          <Clock size={16} className="mr-2" color="#f59e0b" />
                          {formatTime12Hour(event.time)}
                        </div>
                        <div className="flex items-center text-slate-600">
                          <MapPin size={16} className="mr-2" color="#f59e0b" />
                          <span className="truncate">{event.location}</span>
                        </div>
                        <div className="flex items-center text-slate-600">
                          <Users size={16} className="mr-2" color="#f59e0b" />
                          {event.registrations} registered
                        </div>
                      </div>
                      <p className="text-sm text-slate-700 mb-3 line-clamp-2">{event.description}</p>
                      <div className="space-y-2">
                        <button 
                          onClick={() => setRegisterModal({ show: true, event })}
                          className="btn-primary w-full text-sm py-2"
                          data-testid={`register-btn-${event.id}`}
                        >
                          Register
                        </button>
                        <button
                          onClick={() => handleDownloadCalendar(event.id)}
                          className="btn-secondary w-full text-sm py-2"
                          data-testid={`download-calendar-${event.id}`}
                        >
                          <Download size={16} className="inline mr-1" />
                          Calendar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Show More / Show Less Button */}
                {upcomingEvents.length > 4 && (
                  <div className="text-center mt-8">
                    <button
                      onClick={() => setShowAllEvents(!showAllEvents)}
                      className="btn-secondary inline-flex items-center gap-2 px-8 py-3 text-lg"
                      data-testid="show-more-events-btn"
                    >
                      {showAllEvents ? (
                        <>
                          <ChevronUp size={20} />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown size={20} />
                          Show More ({upcomingEvents.length - 4} more events)
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="section-padding bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl md:text-5xl font-bold mb-12" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
              Events Calendar
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {upcomingEvents.map((event) => {
                const eventDate = parseLocalDate(event.date);
                const month = eventDate.toLocaleDateString('en-US', { month: 'short' });
                const day = eventDate.getDate();
                
                return (
                  <div key={event.id} className="card p-6 text-center" data-testid={`calendar-event-${event.id}`}>
                    <div className="bg-blue-900 text-white rounded-lg p-4 mb-4 inline-block">
                      <div className="text-3xl font-bold">{day}</div>
                      <div className="text-lg uppercase">{month}</div>
                    </div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#1e3a8a' }}>{event.title}</h3>
                    <p className="text-lg text-slate-600 mb-2">{formatTime12Hour(event.time)}</p>
                    <p className="text-slate-600 mb-4">{event.location}</p>
                    <div className="space-y-2">
                      <button
                        onClick={() => setRegisterModal({ show: true, event })}
                        className="btn-primary w-full text-base"
                      >
                        Register
                      </button>
                      <button
                        onClick={() => handleDownloadCalendar(event.id)}
                        className="btn-secondary w-full text-base"
                      >
                        <Download size={16} className="inline mr-1" />
                        Add to Calendar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Recurring Events */}
      <div className="section-padding" style={{ backgroundColor: '#fffbeb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-12" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }} data-testid="recurring-events-title">
            Weekly Schedule
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {recurringEvents.map((event) => (
              <div key={event.id} className="card p-6" data-testid={`recurring-${event.id}`}>
                {event.image_url ? (
                  <div 
                    className="relative group cursor-pointer mb-4 overflow-hidden rounded-lg bg-slate-100"
                    onClick={() => openLightbox(event.image_url, event.title)}
                    data-testid={`recurring-image-${event.id}`}
                  >
                    <div className="aspect-[16/9] w-full">
                      <img src={event.image_url} alt={event.title} className="w-full h-full object-contain bg-white transition-transform duration-300 group-hover:scale-105" />
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center rounded-lg">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full p-3 shadow-lg">
                        <ZoomIn size={24} className="text-blue-900" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-[16/9] w-full rounded-lg mb-4 bg-gradient-to-br from-blue-100 to-amber-100 flex items-center justify-center">
                    <Calendar size={64} className="text-blue-300" />
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#1e3a8a' }}>{event.title}</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-lg text-slate-600">
                    <Clock size={20} className="mr-2" color="#f59e0b" />
                    {event.recurrence_pattern}
                  </div>
                  <div className="flex items-center text-lg text-slate-600">
                    <MapPin size={20} className="mr-2" color="#f59e0b" />
                    {event.location}
                  </div>
                </div>
                <p className="text-lg text-slate-700">{event.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Registration Modal */}
      {registerModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="registration-modal">
          <div className="bg-white rounded-xl p-8 max-w-md w-full">
            <h3 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
              Register for {registerModal.event?.title}
            </h3>
            <form onSubmit={handleRegister} className="space-y-6">
              <div>
                <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Your Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                  placeholder="Enter your full name"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Email Address</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                  placeholder="Enter your email"
                  data-testid="register-email-input"
                />
              </div>
              <div className="flex gap-4">
                <button type="submit" className="btn-primary flex-1" data-testid="submit-registration-btn">
                  Register
                </button>
                <button 
                  type="button" 
                  onClick={() => setRegisterModal({ show: false, event: null })}
                  className="btn-secondary flex-1"
                  data-testid="cancel-registration-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        imageUrl={lightboxImage.url}
        imageAlt={lightboxImage.alt}
      />
    </div>
  );
}

export default Events;